// src/Loan/PrintLoanStatement.jsx
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
  fetchLoanEmis,
  clearLoanEmis,
} from "../redux/slices/loan/loanEmiSlice";
import { fetchAllLoans } from "../redux/slices/loan/loanReportSlice";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";

import "../assets/print/PrintLoanStatement.css";
import axios from "axios";

/* ================== Utils ================== */
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return isNaN(date) ? dateStr : date.toLocaleDateString("en-GB");
};

const formatAmount = (amount) =>
  !amount ? "Rs 0" : `Rs ${Number(amount).toLocaleString("en-IN")}`;

/* ================== Component ================== */
const PrintLoanStatement = () => {
  const { loanId } = useParams();
  const dispatch = useDispatch();
  const cardRef = useRef(null);

  const {
    emis,
    loading: emiLoading,
    error: emiError,
  } = useSelector((state) => state.loanEmi);
  const {
    loans,
    loading: loanLoading,
    error: loanError,
  } = useSelector((state) => state.loanReport);
  const { profile } = useSelector((state) => state.userProfile);

  const [company, setCompany] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);

  const [branchSealUrl, setBranchSealUrl] = useState(null);
  const [branchSignUrl, setBranchSignUrl] = useState(null);

  const branchId = Number(localStorage.getItem("userId")) || 0;

  /* ================== Fetch Profile ================== */
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  /* ================== Fetch Loan & EMI ================== */
  useEffect(() => {
    if (!loanId) return;

    dispatch(fetchLoanEmis(loanId));
    if (loans.length === 0) dispatch(fetchAllLoans());

    return () => dispatch(clearLoanEmis());
  }, [dispatch, loanId, loans.length]);

  /* ================== Fetch Company ================== */
  useEffect(() => {
    if (!profile?.superAdminId) return;

    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile.superAdminId}`,
        );
        const data = await res.json();

        if (res.ok && data) {
          setCompany({
            ...data,
            logoUrl: data.logo
              ? `https://payyug.in/${data.logo.replace(/^images\//, "")}`
              : null,
          });
        }
      } catch (err) {
        console.error("Company fetch error:", err);
      }
    };

    fetchCompany();
  }, [profile?.superAdminId]);

  const loan = loans.find((l) => String(l.id) === String(loanId));

  /* ================== Fetch Customer Details & Photo ================== */
  useEffect(() => {
    if (!loan?.customerId || !profile?.superAdminId) return;

    const fetchCustomerDetails = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/customers-get-userId?userId=${loan.customerId}&branchId=${branchId}`,
        );
        const data = await res.json();
        setCustomerDetails(data?.data || null);
      } catch (err) {
        console.error("Customer fetch error:", err);
      }
    };

    const fetchCustomerPhoto = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/list-kyc?userId=${loan.customerId}&superAdminId=${profile.superAdminId}&createdById=${branchId}`,
        );
        const data = await res.json();

        const photoDoc = data?.documents?.find(
          (doc) => doc.docName?.toLowerCase() === "photo",
        );

        if (photoDoc?.docImage) {
          setPhotoUrl(`https://payyug.in/uploads/${photoDoc.docImage}`);
        }
      } catch (err) {
        console.error("Photo fetch error:", err);
      }
    };

    fetchCustomerDetails();
    fetchCustomerPhoto();
  }, [loan?.customerId, profile?.superAdminId, branchId]);

  useEffect(() => {
    const fetchSealSign = async () => {
      if (!profile?.superAdminId) return;

      try {
        const res = await axios.get(
          `https://payyug.in/api/list/stamp?superAdminId=${profile?.superAdminId}&category=Loan Statement`,
        );

        const dataArray = res?.data?.data?.[0] || [];

        if (dataArray && dataArray.file) {
          const cleanSealPath = dataArray.file.replace(/^images\//, "");
          setBranchSealUrl(`https://payyug.in/${cleanSealPath}`);
        } else {
          setBranchSealUrl(null);
        }

        if (dataArray && dataArray.type) {
          const cleanSignPath = dataArray.type.replace(/^images\//, "");
          setBranchSignUrl(`https://payyug.in/${cleanSignPath}`);
        } else {
          setBranchSignUrl(null);
        }
      } catch (error) {
        console.error("API Fetch Error:", error);
      }
    };

    fetchSealSign();
  }, [profile?.superAdminId]);

  /* ================== PDF Generator ================== */
  const generatePDF = async () => {
    if (!cardRef.current) return;

    const actionButtons = document.querySelectorAll(".print-loan-statement-download-btn");
    actionButtons.forEach((btn) => (btn.style.display = "none"));

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const contentHeight = pageHeight - margin * 2;

    const canvas = await html2canvas(cardRef.current, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    });

    const imgWidth = canvas.width;
    const pixelPageHeight = (imgWidth * contentHeight) / contentWidth;

    const breakElements = cardRef.current.querySelectorAll("tr, h2, h3, header, footer");
    const containerRect = cardRef.current.getBoundingClientRect();
    let breakPoints = [];

    breakElements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const relativeTop = (rect.top - containerRect.top) * 1.5;
      breakPoints.push(relativeTop);
    });

    let heightLeft = canvas.height;
    let sY = 0;

    while (heightLeft > 0) {
      let sHeight = pixelPageHeight;

      if (heightLeft > pixelPageHeight) {
        const expectedPageEnd = sY + pixelPageHeight;
        let bestBreakPoint = expectedPageEnd;

        for (let i = 0; i < breakPoints.length; i++) {
          if (breakPoints[i] > sY + 50 && breakPoints[i] < expectedPageEnd) {
            bestBreakPoint = breakPoints[i];
          }
        }

        sHeight = bestBreakPoint - sY;
      } else {
        sHeight = heightLeft;
      }

      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = imgWidth;
      pageCanvas.height = sHeight;

      const ctx = pageCanvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, sY, imgWidth, sHeight, 0, 0, imgWidth, sHeight);

      const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.75);
      const displayHeight_mm = (sHeight * contentWidth) / imgWidth;

      if (sY > 0) pdf.addPage();

      pdf.addImage(
        pageImgData,
        "JPEG",
        margin,
        margin,
        contentWidth,
        displayHeight_mm,
        undefined,
        "FAST"
      );

      sY += sHeight;
      heightLeft -= sHeight;
    }

    actionButtons.forEach((btn) => (btn.style.display = "inline-block"));
    pdf.save(`${loan?.userName || "Customer"}_LoanStatement.pdf`);
  };

  /* ================== Print Logic ================== */
  const triggerPrintElement = (elementRef, title = "Print_Document") => {
    const input = elementRef.current;
    if (!input) return;

    const existingIframe = document.getElementById("print-iframe");
    if (existingIframe) {
      document.body.removeChild(existingIframe);
    }

    const iframe = document.createElement("iframe");
    iframe.id = "print-iframe";
    iframe.style.position = "absolute";
    iframe.style.left = "-9999px";
    iframe.style.top = "0";
    iframe.style.width = "1024px";
    iframe.style.height = "auto";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;

    let extractedStyles = "";
    document.querySelectorAll("style, link[rel='stylesheet']").forEach((node) => {
      extractedStyles += node.outerHTML;
    });

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          ${extractedStyles}
          <style>
            @page {
              size: A4 portrait;
              margin: 8mm 10mm;
            }
            *, *::before, *::after {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              box-sizing: border-box !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            body {
              background: #ffffff !important;
              color: #000000 !important;
              font-family: Arial, Helvetica, sans-serif !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-loan-statement-container {
              width: 100% !important;
              margin: 0 auto !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
              display: block !important;
            }
            .print-loan-statement-header {
              display: flex !important;
              flex-direction: row !important;
              justify-content: space-between !important;
              align-items: center !important;
              padding: 10px 14px !important;
              border-bottom: 1.5px solid #000000 !important;
            }
            .print-loan-statement-logo-box {
              width: 85px !important;
              height: 85px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              overflow: hidden !important;
              flex-shrink: 0 !important;
            }
            .print-loan-statement-logo {
              max-width: 100% !important;
              max-height: 100% !important;
              width: auto !important;
              height: auto !important;
              object-fit: contain !important;
              border-radius: 50% !important;
              display: block !important;
            }
            .print-loan-statement-details-box {
              text-align: center !important;
              flex: 1 !important;
            }
            .print-loan-statement-details-box h2 {
              font-size: 16px !important;
              font-weight: bold !important;
              margin: 0 0 4px 0 !important;
              color: #000000 !important;
            }
            .print-loan-statement-details-box p {
              font-size: 10px !important;
              line-height: 1.35 !important;
              margin: 0 !important;
              color: #000000 !important;
            }
            .print-loan-statement-title-section {
              text-align: center !important;
              margin: 10px 0 !important;
            }
            .print-loan-statement-title {
              font-size: 14px !important;
              font-weight: bold !important;
              text-decoration: underline !important;
              margin: 0 !important;
            }
            table, .print-loan-statement-detail-table, .print-loan-statement-table {
              width: 100% !important;
              border-collapse: collapse !important;
              color: #000000 !important;
              margin-bottom: 10px !important;
            }
            th, td {
              border: 1px solid #000000 !important;
              padding: 5px 6px !important;
              font-size: 10px !important;
              color: #000000 !important;
            }
            th {
              background-color: #f1f5f9 !important;
              font-weight: bold !important;
            }
            tr {
              page-break-inside: avoid !important;
            }
            .branch-loan-card-footer {
              display: flex !important;
              flex-direction: row !important;
              justify-content: space-between !important;
              page-break-inside: avoid !important;
              margin-top: 15px !important;
              padding: 10px 0 !important;
            }
            .box-size {
              flex: 1 !important;
              text-align: center !important;
              font-size: 10px !important;
              font-weight: 600 !important;
            }
            .border-line {
              border-top: 1px dashed #000000 !important;
              margin-top: 20px !important;
            }
            .footer-seal-sign {
              position: relative !important;
            }
            .footer-seal img {
              width: 60px !important;
              height: 60px !important;
              object-fit: contain !important;
              margin: 4px auto 0 !important;
              display: block !important;
            }
            .footer-sign img {
              width: 110px !important;
              height: 35px !important;
              object-fit: contain !important;
              margin: 4px auto 0 !important;
              display: block !important;
            }
            img {
              display: inline-block !important;
              visibility: visible !important;
              max-width: 100% !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            button, .print-loan-statement-download-btn, [class*="download-btn"], [class*="print-btn"] {
              display: none !important;
            }
          </style>
        </head>
        <body>
          <div class="print-loan-statement-container">
            ${input.innerHTML}
          </div>
        </body>
      </html>
    `);
    doc.close();

    const checkAndPrint = () => {
      const iframeImages = doc.querySelectorAll("img");
      const promises = Array.from(iframeImages).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });

      Promise.all(promises).then(() => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 250);
      });
    };

    if (doc.readyState === "complete") {
      checkAndPrint();
    } else {
      iframe.contentWindow.onload = checkAndPrint;
    }
  };

  const handlePrint = () => {
    triggerPrintElement(
      cardRef,
      `${loan?.userName || "Customer"}_LoanStatement`
    );
  };

  /* ================== UI States ================== */
  if (loanLoading || emiLoading)
    return <div className="print-loan-statement-container">Loading...</div>;

  if (loanError || emiError)
    return (
      <div className="print-loan-statement-container">
        {loanError || emiError}
      </div>
    );

  if (!loan)
    return (
      <div className="print-loan-statement-container">
        No loan found for Loan ID {loanId}
      </div>
    );

  /* ================== JSX ================== */
  return (
    <div>
      <div className="print-loan-statement-container" ref={cardRef}>
        <header className="print-loan-statement-header">
          <div className="print-loan-statement-logo-box">
            {company?.logoUrl && (
              <img
                src={company.logoUrl}
                alt="Logo"
                className="print-loan-statement-logo"
              />
            )}
          </div>

          <div className="print-loan-statement-details-box">
            <h2>{company?.name || "Company Name"}</h2>
            <p>
              <strong>CIN:</strong> {company?.cin || "-"}
              <br />
              <strong>Regt. Office:</strong> {company?.address || "-"}
              <br />
              <strong>Website:</strong> {company?.domain || "-"}
              <br />
              <strong>Contact:</strong> {company?.contact || "-"}
              <br />
              <strong>Email:</strong> {company?.email || "-"}
            </p>
          </div>
          <div style={{ width: "85px", flexShrink: 0 }}></div>
        </header>

        <div className="print-loan-statement-title-section">
          <h3 className="print-loan-statement-title">Loan Statement</h3>
        </div>

        <table className="print-loan-statement-detail-table">
          <tbody>
            <tr>
              <td>
                <strong>Customer Name:</strong> {loan.userName || "-"}
              </td>
              <td>
                <strong>Account Number:</strong>{" "}
                {loan.loanAccountNumber || loan.loanNumber}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Branch:</strong> {profile?.branchName || "-"}
              </td>
              <td>
                <strong>Date:</strong> {new Date().toLocaleDateString("en-GB")}
              </td>
            </tr>
          </tbody>
        </table>

        <section className="print-loan-statement-table-section">
          <table className="print-loan-statement-table">
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>EMI Amount</th>
                {loan?.loanType?.toLowerCase()==="personal" && (
                  <>
                <th>Principal</th>
                <th>Interest</th>
                  </>
                )}
                <th>Emi Due Date</th>
                <th>Payment Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {emis.map((emi, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{formatAmount(emi.emiAmount)}</td>
                  {loan?.loanType?.toLowerCase()==="personal"&&(
                    <>
                  <td>{formatAmount(emi.principalComponent)}</td>
                  <td>{formatAmount(emi.interestComponent)}</td>
                    </>
                  )}
                  <td>{formatDate(emi.dueDate)}</td>
                  <td>{formatDate(emi.paymentDate)}</td>
                  <td>{emi.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <footer className="branch-loan-card-footer print-row">
          <div className="box-size">
            Member Signature
            <br />
            (सदस्य के हस्ताक्षर)
            <div className="border-line"></div>
          </div>
          <div className="box-size">
            Spouse Signature
            <br />
            (जीवनसाथी के हस्ताक्षर)
            <div className="border-line"></div>
          </div>
          <div className="footer-seal-sign box-size">
            Branch Seal With Signature
            <br />
            (शाखा मोहर के साथ हस्ताक्षर)
            <div className="border-line"></div>
            {branchSealUrl && (
              <div className="footer-seal">
                <img src={branchSealUrl} alt="Seal" />
              </div>
            )}
            {branchSignUrl && (
              <div className="footer-sign">
                <img src={branchSignUrl} alt="Signature" />
              </div>
            )}
          </div>
        </footer>
      </div>

      <div
        style={{
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          marginTop: "16px",
        }}
      >
        <button
          onClick={handlePrint}
          className="print-loan-statement-download-btn"
          style={{ cursor: "pointer" }}
        >
          🖨️ Print Statement
        </button>

        <button
          onClick={generatePDF}
          className="print-loan-statement-download-btn"
          style={{ cursor: "pointer" }}
        >
          📄 Download PDF
        </button>
      </div>
    </div>
  );
};

export default PrintLoanStatement;